import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Shared pagination state for library browse pages. page + per_page live
// in the URL query string (?page=2&per_page=50), so a paginated view is
// linkable, back/forward works, and a refresh lands on the same page.
// per_page defaults to DEFAULT_PER_PAGE (50); when the server has a
// stored ui.pagination.per_page setting, callers pass it as storedPerPage
// and omitting per_page in the URL then means "use the stored one".

export const DEFAULT_PER_PAGE = 50;

export const PER_PAGE_CHOICES = [25, 50, 100, 250] as const;

export interface PagedParams {
    page: number;
    perPage: number;
}

function parseIntOr(value: string | null, fallback: number): number {
    if (value === null || value === '') return fallback;
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
}

// clampPerPage bounds a page size so a stale/abusive URL cannot ask for
// the whole library in one shot (mirrors the server's MaxPerPage cap).
export function clampPerPage(value: number): number {
    if (value < 1) return 1;
    if (value > 500) return 500;
    return value;
}

// resolvePerPage picks the effective page size: an explicit per_page in
// the URL wins; otherwise the server-stored setting; otherwise the
// built-in default of 50.
function resolvePerPage(urlValue: string | null, storedPerPage?: number): number {
    if (urlValue !== null && urlValue !== '') {
        return clampPerPage(parseIntOr(urlValue, DEFAULT_PER_PAGE));
    }
    if (typeof storedPerPage === 'number' && storedPerPage >= 1) {
        return clampPerPage(storedPerPage);
    }
    return DEFAULT_PER_PAGE;
}

export interface UsePagedUrlOptions {
    /** Server-stored default (ui.pagination.per_page); omit when unknown. */
    storedPerPage?: number;
}

export interface UsePagedUrlResult extends PagedParams {
    /** 1..totalPages after the caller knows the total; clamped on navigation. */
    setPage: (page: number) => void;
    setPerPage: (perPage: number) => void;
}

// usePagedUrl owns the ?page / ?per_page URL params for one browse page.
export function usePagedUrl({ storedPerPage }: UsePagedUrlOptions = {}): UsePagedUrlResult {
    const location = useLocation();
    const navigate = useNavigate();

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const perPage = resolvePerPage(params.get('per_page'), storedPerPage);
    // A page below 1 still renders (callers clamp against totalPages on
    // navigation); this keeps the initial render stable before data lands.
    const page = Math.max(parseIntOr(params.get('page'), 1), 1);

    const patch = useCallback(
        (next: { page?: number; per_page?: number }) => {
            const merged = new URLSearchParams(params);
            for (const [key, value] of Object.entries(next)) {
                merged.set(key, String(value));
            }
            navigate({ pathname: location.pathname, search: merged.toString() }, { replace: false });
        },
        [navigate, location.pathname, params]
    );

    const setPage = useCallback(
        (next: number) => {
            patch({ page: Math.max(next, 1) });
        },
        [patch]
    );

    const setPerPage = useCallback(
        (next: number) => {
            // Changing page size resets to page 1: any other page number
            // would point at a different slice of the list.
            const merged = new URLSearchParams(params);
            merged.set('per_page', String(clampPerPage(next)));
            merged.set('page', '1');
            navigate({ pathname: location.pathname, search: merged.toString() }, { replace: false });
        },
        [navigate, location.pathname, params]
    );

    return { page, perPage, setPage, setPerPage };
}

// totalPageCount derives totalPages from a total count - shared by the
// Pagination control and pages clamping an out-of-range URL page.
export function totalPageCount(total: number, perPage: number): number {
    if (total <= 0) return 1;
    return Math.max(Math.ceil(total / perPage), 1);
}
